import once from 'lodash/once'
import { v4 } from 'uuid'

import type { FirebaseAnnouncementResponse, FirebaseDirectAnnouncement, FirebaseRequest } from '@app/constants/ApiTypes/firebaseEntities'

import { bufferedAction } from '@app/utils/bufferedAction'
import { EventBus } from '@app/utils/eventbus'
import { createLogger } from '@app/utils/loggerCreator'
import { retry } from '@app/utils/retry'
import { uppercaseFirstLetter } from '@app/utils/uppercaseFirstLetter'

import { withAbortSignal } from '@app/packages/abortContext/actions'
import { dispose } from '@app/packages/disposable/disposable'
import { spawn } from '@app/packages/disposable/spawn'

import { applyFirebaseBatch } from '@app/store/actions/firebase'
import { firebaseTokenSelector, setFirebaseToken } from '@app/store/slices/firebase_token'
import { unsetNewsSync } from '@app/store/slices/news'
import { requestsWithDebtSyncSlice } from '@app/store/slices/requests'
import type { Store } from '@app/store/store'
import type { FirebaseBatch } from '@app/store/types/firebase'

import type { FirebaseManager } from './FirebaseManager'

export class FirebaseDatabaseManager {
  private firebaseManager: FirebaseManager
  private store: Store
  private eventBus = new EventBus<FirebaseEvent>()
  private disposable: Disposable | null = null

  constructor(firebaseManager: FirebaseManager, store: Store) {
    this.firebaseManager = firebaseManager
    this.store = store
    this.setupListeners()
    this.disposable = this.setup()
  }

  [Symbol.dispose]() {
    if (this.disposable) {
      dispose(this.disposable)
    }
  }

  addEventListener(cb: (event: FirebaseEvent) => void) {
    return this.eventBus.on(cb)
  }

  private setup() {
    return spawn(
      async stack => {
        const ab = stack.adopt(new AbortController(), ab => ab.abort())
        this.log.log('Connecting...')
        stack.defer(() => this.log.log('Disconnected'))
        const [firebase] = await Promise.all([this.loader(), this.firebaseManager.connect()])
        ab.signal.throwIfAborted()
        const { auth, database } = firebase

        const firebaseToken = await this.getToken(ab.signal)
        ab.signal.throwIfAborted()

        await firebase.auth.signInWithCustomToken(auth.getAuth(), firebaseToken)
        ab.signal.throwIfAborted()

        const refs = await this.getRefs()
        ab.signal.throwIfAborted()

        stack.adopt(
          database.onChildAdded(refs.events, data => {
            const val: { data?: any } = data.val()
            if (!val?.data?.type) return
            this.log.log('child_added:', 'event', data.key, val)
            this.eventBus.dispatch(val.data)
          }),
          unsub => unsub()
        )

        stack.adopt(
          database.onChildAdded(refs.events_broadcast, data => {
            const val: { data?: any } = data.val()
            if (!val?.data?.type) return
            this.log.log('child_added:', 'event_broadcast', data.key, val)
            this.eventBus.dispatch(val.data)
          }),
          unsub => unsub()
        )

        for (const entity of ['requests', 'announcement_responses', 'direct_announcements'] as const) {
          for (const type of ['added', 'changed', 'removed'] as const) {
            const method = `onChild${uppercaseFirstLetter(type)}` as `onChild${'Added' | 'Changed' | 'Removed'}`
            stack.adopt(
              database[method](refs[entity], data => {
                const payload = data.val()
                this.log.log(`child_${type}:`, entity, data.key, payload)
                this.eventBus.dispatch({ entity, type, payload })
              }),
              unsub => unsub()
            )
          }
        }

        stack.adopt(
          database.onChildChanged(refs.news, data => {
            this.log.log('child_changed:', 'news', data.key, data.val())
            this.eventBus.dispatch({ type: 'news_changed', payload: { updatedCount: data.val() } })
          }),
          unsub => unsub()
        )

        this.log.log('Connected:', auth.getAuth().currentUser)
      },
      e => {
        this.log.log('Connected failed:', e)
      }
    )
  }

  private setupListeners() {
    const mutateBuffer = bufferedAction(
      (): FirebaseBatch => ({
        requests: {
          models: {},
          added: new Set(),
          changed: new Set(),
          removed: new Set(),
        },
        direct_announcements: {
          models: {},
          added: new Set(),
          changed: new Set(),
          removed: new Set(),
        },
        announcement_responses: {
          models: {},
          added: new Set(),
          changed: new Set(),
          removed: new Set(),
        },
      }),
      buffer => {
        this.store.dispatch(applyFirebaseBatch(buffer))
      },
      200
    )

    this.eventBus.on(e => {
      switch (e.type) {
        case 'news_changed':
          this.store.dispatch(unsetNewsSync())
          return
        case 'added':
        case 'changed':
        case 'removed':
          mutateBuffer(buffer => {
            if (e.type === 'removed') {
              delete buffer[e.entity].models[e.payload.id]
              buffer[e.entity].added.delete(e.payload.id)
              buffer[e.entity].changed.delete(e.payload.id)
              buffer[e.entity].removed.add(e.payload.id)
            } else {
              buffer[e.entity].models[e.payload.id] = e.payload
              buffer[e.entity][e.type].add(e.payload.id)
              buffer[e.entity].removed.delete(e.payload.id)
            }
            return buffer
          })
          return
        case 'debt_changed':
          this.store.dispatch(requestsWithDebtSyncSlice.set({ hash: v4() }))
          break
        default:
          this.log.log('Unknown event:', e)
      }
    })
  }

  private loader = once(async () => {
    const [app, database, auth] = await Promise.all([import('firebase/app'), import('firebase/database'), import('firebase/auth')])
    const firebase = { app, database, auth }
    return firebase
  })

  private async getToken(abortSignal: AbortSignal): Promise<string> {
    const { dispatch, getState } = this.store

    const stateToken = firebaseTokenSelector(getState())
    if (stateToken) return stateToken

    return retry(
      async () => {
        const [{ postFirebaseTokens }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/firebase'), import('@app/store/actions/api')])
        const token = (await dispatch(withAbortSignal(abortSignal, callV2Api(postFirebaseTokens())))).data
        if (!token) throw new Error('Firebase token is undefined')
        dispatch(setFirebaseToken(token.id))
        return token.id
      },
      { interval: 5000, signal: abortSignal }
    )
  }

  private async getRefs() {
    const user = this.firebaseManager.user
    const { getDatabase, ref, query, orderByChild, equalTo } = await this.loader().then(m => m.database)
    const accountType = user.account_type.toLocaleLowerCase()
    const db = getDatabase()

    const requestsRef = ref(db, 'requests')
    const announcementResponsesRef = ref(db, 'announcement_responses')
    const directAnnouncementsRef = ref(db, 'direct_announcements')
    const filteredRequests = query(requestsRef, orderByChild('attributes/' + accountType + '/id'), equalTo(user.id))
    const filteredAnnouncementResponses = query(announcementResponsesRef, orderByChild('attributes/' + accountType + '/id'), equalTo(user.id))
    const filteredDirectAnnouncements = query(directAnnouncementsRef, orderByChild('attributes/' + accountType + '/id'), equalTo(user.id))

    const newsRef = ref(db, 'news')

    const eventsRef = ref(db, `events/${user.id}`)
    const eventsBroadcastRef = ref(db, 'events/broadcasts')

    return {
      requests: filteredRequests,
      announcement_responses: filteredAnnouncementResponses,
      direct_announcements: filteredDirectAnnouncements,
      news: newsRef,
      events: eventsRef,
      events_broadcast: eventsBroadcastRef,
    }
  }

  private log = createLogger('FirebaseDatabase', 'orange', 'bold')
}

export type FirebaseEvent =
  | {
      type: 'debt_changed'
    }
  | {
      type: 'news_changed'
      payload: { updatedCount: number }
    }
  | {
      type: 'added' | 'removed' | 'changed'
      entity: 'requests'
      payload: FirebaseRequest
    }
  | {
      type: 'added' | 'removed' | 'changed'
      entity: 'direct_announcements'
      payload: FirebaseDirectAnnouncement
    }
  | {
      type: 'added' | 'removed' | 'changed'
      entity: 'announcement_responses'
      payload: FirebaseAnnouncementResponse
    }

export type FirebaseEventHandler = (data: FirebaseEvent) => unknown
